import React, { useState, useRef } from 'react';
import TOKENS from '../data/tokens';
import { chatWithOscar, OSCAR_AVATAR_URL } from '../data/api';
import { Send, X } from 'lucide-react';

const quickQs = [
  "What's the date of the event?",
  "Where is it being held?",
  "What should I wear?",
  "Can I bring a plus one?",
  "Is there parking available?",
];

const OscarButton = ({ embedded = false }) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => 'oscar-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8));
  const messagesEndRef = useRef(null);

  // Apr 2026: legacy intro animation video (oscar_animation.mp4) removed —
  // it contained the old dog-themed Oscar. Click now opens the chat directly.
  const handleOscarClick = () => {
    setChatOpen(o => !o);
  };

  const sendMsg = async (text) => {
    const msg = text || input;
    if (!msg.trim()) return;
    setInput('');
    // Carry the last ~6 prior display turns so Oscar keeps conversation
    // context on short follow-ups (e.g. "yes" after a clarifying question).
    // Built from the messages already on screen, BEFORE this new turn is
    // appended. Maps the display shape (from:'user'|'oscar') to the API
    // shape (role:'user'|'assistant').
    const history = messages
      .slice(-6)
      .map(m => ({ role: m.from === 'oscar' ? 'assistant' : 'user', text: m.text }));
    setMessages(prev => [...prev, { from:'user', text:msg }]);
    setSending(true);
    try {
      const res = await chatWithOscar(msg, sessionId, history);
      setMessages(prev => [...prev, { from:'oscar', text: res.reply }]);
    } catch {
      setMessages(prev => [...prev, { from:'oscar', text: 'My apologies — something went wrong. Please try again in a moment.' }]);
    } finally {
      setSending(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }), 100);
    }
  };

  return (
    <>
      {/* Soft gold ring/pulse keyframes — local to this component, applied
          only when the floating Oscar avatar is at rest (chat closed, not
          animating). Tasteful, premium, no layout shift. */}
      <style>{`
        @keyframes oscarGoldPulse {
          0%   { transform: translate(-50%, -50%) scale(0.94); opacity: 0.55; }
          70%  { transform: translate(-50%, -50%) scale(1.18); opacity: 0;    }
          100% { transform: translate(-50%, -50%) scale(1.18); opacity: 0;    }
        }
        @keyframes oscarGoldRingHalo {
          0%, 100% { box-shadow: 0 0 0 1.5px rgba(200,170,60,0.55), 0 0 12px rgba(200,170,60,0.18); }
          50%      { box-shadow: 0 0 0 1.5px rgba(220,185,80,0.78), 0 0 18px rgba(220,185,80,0.32); }
        }
        /* Mobile-only correction (Apr 2026): make the floating Oscar chat
           card meaningfully taller on phones, lift the messages-region cap,
           and bump body font-size so guests aren't squinting. Desktop is
           unchanged at all widths >= 641px.

           Uses width + right + bottom only (no left/inset) so the card
           sizes against the viewport even if an ancestor establishes a
           containing block via transform or filter. */
        @media (max-width: 640px) {
          [data-testid="oscar-chat-modal"] {
            top: auto !important;
            left: auto !important;
            right: 8px !important;
            bottom: 12px !important;
            width: calc(100vw - 16px) !important;
            max-width: calc(100vw - 16px) !important;
            max-height: calc(100dvh - 24px) !important;
            min-height: 78vh !important;
          }
          /* Lift the hard-coded 300px cap on the messages region so the
             extra vertical room actually flows into the conversation area. */
          [data-testid="oscar-chat-modal"] > div:nth-child(2) {
            max-height: none !important;
            font-size: 14.5px !important;
          }
          [data-testid="oscar-input"] {
            font-size: 14.5px !important;
          }
        }
        /* Readable placeholder on the now-white input (no weak gray-on-gray). */
        [data-testid="oscar-input"]::placeholder {
          color: #5A5A60;
          opacity: 1;
        }
      `}</style>

      {/* Chat Modal - right side */}
      {chatOpen && (
        <div data-testid="oscar-chat-modal" style={{
          position: 'fixed', bottom: 100, right: 20, width: 340, maxHeight: 480,
          background: '#FFFFFF', borderRadius: 20,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: `1px solid ${TOKENS.colors.borderGrey}`,
        }}>
          {/* Header */}
          <div style={{ background: '#333', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={OSCAR_AVATAR_URL} alt="Oscar" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center top', border: '2px solid rgba(255,255,255,0.2)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: TOKENS.colors.textWhite, fontSize: 14, fontWeight: 600 }}>Oscar — The M-Vitations Concierge</div>
            </div>
            <button data-testid="oscar-close-btn" onClick={() => setChatOpen(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: TOKENS.colors.textWhite, cursor: 'pointer', padding: 6, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, maxHeight: 300, background: '#FFFFFF' }}>
            {messages.length === 0 && (
              <div style={{ background: '#F7F7F8', borderRadius: 14, padding: '12px 14px', fontSize: 13, color: TOKENS.colors.textBlack, lineHeight: 1.6, marginBottom: 10, border: `1px solid ${TOKENS.colors.borderGrey}` }}>
                I'm Oscar, the M-Vitations concierge. I'm here to help make Carol's celebration feel effortless. How can I help?
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.from === 'user' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                <div style={{
                  background: m.from === 'user' ? TOKENS.colors.buttonGold : '#F7F7F8',
                  color: m.from === 'user' ? TOKENS.colors.textWhite : TOKENS.colors.textBlack,
                  padding: '10px 14px', borderRadius: 14, maxWidth: '80%', fontSize: 13, lineHeight: 1.5,
                  border: m.from === 'oscar' ? `1px solid ${TOKENS.colors.borderGrey}` : 'none',
                }}>{m.text}</div>
              </div>
            ))}
            {sending && <div style={{ display: 'flex' }}><div style={{ background: '#F7F7F8', padding: '10px 14px', borderRadius: 14, fontSize: 13, color: '#5A5A60', border: `1px solid ${TOKENS.colors.borderGrey}` }}>Typing...</div></div>}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length === 0 && (
            <div style={{ padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 6, borderTop: `1px solid ${TOKENS.colors.borderGrey}`, background: '#FFFFFF' }}>
              {quickQs.map((q, i) => (
                <button key={i} data-testid={`quick-q-${i}`} onClick={() => sendMsg(q)} style={{
                  background: '#F7F7F8', border: `1px solid ${TOKENS.colors.borderGrey}`, borderRadius: 16,
                  padding: '6px 12px', fontSize: 11, color: TOKENS.colors.textBlack, cursor: 'pointer', transition: 'all 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#EFEEF1'; e.currentTarget.style.borderColor = TOKENS.colors.textGold; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#F7F7F8'; e.currentTarget.style.borderColor = TOKENS.colors.borderGrey; }}
                >{q}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: 10, borderTop: `1px solid ${TOKENS.colors.borderGrey}`, display: 'flex', gap: 8, background: '#FFFFFF' }}>
            <input data-testid="oscar-input" style={{ flex: 1, border: `1px solid ${TOKENS.colors.borderGrey}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, outline: 'none', fontFamily: 'inherit', background: '#FFFFFF', color: TOKENS.colors.textBlack }}
              placeholder="Ask me anything..." value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMsg()} maxLength={500} />
            <button data-testid="oscar-send-btn" onClick={() => sendMsg()} disabled={sending || !input.trim()} style={{
              background: TOKENS.colors.buttonGold, color: TOKENS.colors.textWhite, border: 'none', borderRadius: 10,
              width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: sending || !input.trim() ? 'default' : 'pointer', opacity: sending || !input.trim() ? 0.5 : 1,
            }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Oscar Button — fixed when floating, static when embedded in footer */}
      <div data-testid="oscar-btn" onClick={handleOscarClick} style={{
        ...(embedded ? {
          position: 'relative',
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
        } : {
          position: 'fixed',
          right: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: 'pointer',
          zIndex: 999,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }),
      }}>
        {/* Oscar container - TRANSPARENT background */}
        <div style={{
          width: 85, 
          height: 85, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
        }}>
          {/* Static Oscar avatar (always shown — legacy intro video removed) */}
          <div style={{
            position: 'relative',
            width: 80, 
            height: 80, 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {/* Outward-radiating soft pulse — runs only when chat closed.
                Sits behind the image, pointer-events none. */}
            {!chatOpen && (
              <div aria-hidden="true" style={{
                position: 'absolute',
                top: '50%', left: '50%',
                width: 80, height: 80,
                borderRadius: '50%',
                pointerEvents: 'none',
                background: 'radial-gradient(circle, rgba(220,185,80,0.32) 0%, rgba(200,170,60,0.10) 55%, transparent 75%)',
                animation: 'oscarGoldPulse 2.6s cubic-bezier(0.4, 0, 0.2, 1) infinite',
                zIndex: 0,
              }} />
            )}
            <img src={OSCAR_AVATAR_URL} alt="Oscar" style={{ 
              position: 'relative',
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              objectPosition: 'center top',
              borderRadius: '50%',
              filter: TOKENS.shadows.oscar,
              animation: chatOpen ? 'none' : 'oscarGoldRingHalo 3.4s ease-in-out infinite',
              zIndex: 1,
            }} />
          </div>
        </div>
        
        {/* "Ask Oscar" label below the image */}
        <div style={{ 
          marginTop: 6,
          fontSize: TOKENS.fontSizes.oscarLabel, 
          fontWeight: TOKENS.fontWeights.medium, 
          color: TOKENS.colors.textGoldDark,
          textShadow: '0 1px 2px rgba(255,255,255,0.8)',
        }}>
          Ask Oscar
        </div>
      </div>
    </>
  );
};

export default OscarButton;
